import { useMemo, useState } from 'react';
import { Alert, Box, Card, CardContent, Chip, CircularProgress, Collapse, Grid, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionIcon from '@mui/icons-material/Description';
import loanConditionsApi from '../../../services/loanConditionsApi';
import loanDocumentsApi from '../../../services/loanDocumentsApi';
import StatusBadge, { CONDITION_STATUS_MAP, CONDITION_APPROVAL_MAP, SEVERITY_MAP } from '../../../components/shared/StatusBadge';

const TIMING_LABELS = {
  PriorToApproval: 'Prior to Approval',
  PriorToDocs: 'Prior to Docs',
  PriorToClosing: 'Prior to Closing',
  PriorToFunding: 'Prior to Funding',
  PostFunding: 'Post Funding',
};

import DataItem from '../../../components/shared/DataItem';
import LoadingSkeleton from '../../../components/shared/LoadingSkeleton';

/** Same display rules as DocumentsTab — aligns with GET /loans/{id}/documents. */
function buildDocumentByIdMap(documents) {
  const map = new Map();
  for (const d of documents || []) {
    if (d?.id != null) map.set(String(d.id).toLowerCase(), d);
  }
  return map;
}

function getDocumentDisplayName(doc) {
  if (!doc) return '';
  return doc.nameOverride || doc.documentTypeName || doc.files?.[0]?.name || 'Document';
}

const ConditionsTab = ({ loanId }) => {
  const { data, isLoading, isFetching, error, refetch } = loanConditionsApi.endpoints.getLoanConditions.useQuery(loanId, { skip: !loanId });
  const { data: docsData, isLoading: docsLoading } = loanDocumentsApi.endpoints.getLoanDocuments.useQuery(loanId, { skip: !loanId });
  const documentById = useMemo(() => buildDocumentByIdMap(docsData?.documents), [docsData]);
  const [filterTiming, setFilterTiming] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  if (isLoading) {
    return <LoadingSkeleton variant="table" rows={6} />;
  }

  if (error) {
    return <Alert severity="error">Failed to load conditions. {error.data?.message || error.data || ''}</Alert>;
  }

  const conditions = data?.conditions || [];

  const filtered = conditions.filter(c => {
    if (filterTiming !== 'all' && c.timing !== filterTiming) return false;
    if (filterStatus !== 'all' && c.conditionStatus !== filterStatus) return false;
    return true;
  });

  // Summary counts by condition status
  const countByConditionStatus = (status) => conditions.filter(c => c.conditionStatus === status).length;
  const countByTiming = (timing) => conditions.filter(c => c.timing === timing).length;


  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle1" fontWeight={700}>Underwriting Conditions ({conditions.length})</Typography>
          <IconButton size="small" onClick={refetch} disabled={isFetching} title="Refresh conditions">
            {isFetching ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Stack>
      </Stack>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Not Ready to Approve', count: countByConditionStatus('NotReadyToApprove'), color: 'warning.main' },
          { label: 'Ready to Approve', count: countByConditionStatus('ReadyToApprove'), color: 'info.main' },
          { label: 'Approved', count: countByConditionStatus('Approved'), color: 'success.main' },
          { label: 'Cancelled', count: countByConditionStatus('AutomaticallyCancelled') + countByConditionStatus('ManuallyCancelled'), color: 'text.secondary' },
        ].map(({ label, count, color }) => (
          <Grid key={label} size={{ xs: 6, md: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h4" color={color}>{count}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Timing breakdown */}
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
        {Object.entries(TIMING_LABELS).map(([key, label]) => {
          const count = countByTiming(key);
          if (count === 0) return null;
          return <Chip key={key} label={`${label}: ${count}`} size="small" variant="outlined" color="info" />;
        })}
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField select size="small" value={filterTiming} onChange={e => setFilterTiming(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="all">All Timings</MenuItem>
          {Object.entries(TIMING_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
        <TextField select size="small" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="all">All Statuses</MenuItem>
          {Object.entries(CONDITION_STATUS_MAP).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
        </TextField>
        <Typography variant="caption" color="text.secondary">Showing {filtered.length} of {conditions.length}</Typography>
      </Stack>

      {/* Conditions Table */}
      {filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <Typography variant="h6" color="text.secondary">No conditions found</Typography>
          <Typography variant="body2" color="text.secondary">
            {conditions.length === 0 ? 'No underwriting conditions have been created for this loan.' : 'No conditions match the selected filters.'}
          </Typography>
        </Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Condition</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Timing</TableCell>
              <TableCell>Task Status</TableCell>
              <TableCell>Condition Status</TableCell>
              <TableCell>Docs</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(c => (
              <ConditionRow
                key={c.id}
                condition={c}
                expanded={expandedId === c.id}
                onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
                documentById={documentById}
                docsLoading={docsLoading}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

const ConditionRow = ({ condition: c, expanded, onToggle, documentById, docsLoading }) => {
  const docSummary =
    c.associatedDocumentIds?.length > 0
      ? c.associatedDocumentIds
          .map((id) => {
            const doc = documentById.get(String(id).toLowerCase());
            return doc ? getDocumentDisplayName(doc) : null;
          })
          .filter(Boolean)
          .join(', ')
      : '';

  return (
    <>
      <TableRow onClick={onToggle} sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            {expanded ? <ExpandMoreIcon fontSize="small" color="action" /> : <ChevronRightIcon fontSize="small" color="action" />}
            <Box>
              <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: 400 }}>{c.name || '—'}</Typography>
              {c.objectiveName && <Typography variant="caption" color="text.secondary">{c.objectiveName}</Typography>}
            </Box>
          </Stack>
        </TableCell>
        <TableCell>{c.category ? <Chip label={c.category} size="small" variant="outlined" color="info" /> : '—'}</TableCell>
        <TableCell>{c.timing ? TIMING_LABELS[c.timing] || c.timing : '—'}</TableCell>
        <TableCell><StatusBadge status={c.status} map={CONDITION_STATUS_MAP} /></TableCell>
        <TableCell><StatusBadge status={c.conditionStatus} map={CONDITION_APPROVAL_MAP} /></TableCell>
        <TableCell>
          {c.associatedDocumentIds?.length > 0 ? (
            <Chip
              icon={<AttachFileIcon />}
              label={c.associatedDocumentIds.length}
              size="small"
              variant="outlined"
              color="info"
              title={docSummary || (docsLoading ? 'Loading document names…' : undefined)}
            />
          ) : '—'}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6} sx={{ bgcolor: 'action.hover' }}>
            <ConditionDetail condition={c} documentById={documentById} docsLoading={docsLoading} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const ConditionDetail = ({ condition: c, documentById, docsLoading }) => {
  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{c.name}</Typography>

          {c.externalFacingMessage && (
            <Alert severity="info" sx={{ mb: 2 }}><strong>External Message:</strong> {c.externalFacingMessage}</Alert>
          )}

          {c.internalInstructions && (
            <Alert severity="warning" sx={{ mb: 2 }}><strong>Internal Instructions:</strong> {c.internalInstructions}</Alert>
          )}

          <Grid container spacing={1}>
            <Grid size={{ xs: 6, sm: 4 }}><DataItem label="Task Type">{c.taskType || '—'}</DataItem></Grid>
            {c.severity && <Grid size={{ xs: 6, sm: 4 }}><Box sx={{ py: 0.5 }}><Typography variant="caption" color="text.secondary">Severity</Typography><Box><StatusBadge status={c.severity} map={SEVERITY_MAP} /></Box></Box></Grid>}
            {c.source && <Grid size={{ xs: 6, sm: 4 }}><DataItem label="Source">{c.source}</DataItem></Grid>}
            <Grid size={{ xs: 6, sm: 4 }}><DataItem label="Manual">{c.isManual ? 'Yes' : 'No'}</DataItem></Grid>
            {c.startedAt && <Grid size={{ xs: 6, sm: 4 }}><DataItem label="Started">{new Date(c.startedAt).toLocaleString()}</DataItem></Grid>}
            {c.completedAt && <Grid size={{ xs: 6, sm: 4 }}><DataItem label="Completed">{new Date(c.completedAt).toLocaleString()}</DataItem></Grid>}
            {c.firstReadyAt && <Grid size={{ xs: 6, sm: 4 }}><DataItem label="First Ready">{new Date(c.firstReadyAt).toLocaleString()}</DataItem></Grid>}
          </Grid>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          {c.notes && c.notes.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Notes</Typography>
              {c.notes.map((note, idx) => (
                <Box key={note.id || idx} sx={{ py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary">{note.text}</Typography>
                  {note.createdAt && <Typography variant="caption" color="text.secondary">{new Date(note.createdAt).toLocaleString()}</Typography>}
                </Box>
              ))}
            </Box>
          )}

          {c.associatedDocumentIds && c.associatedDocumentIds.length > 0 && (
            <Box sx={{ mt: c.notes?.length ? 2 : 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Associated Documents</Typography>
              {c.associatedDocumentIds.map((docId) => {
                const doc = documentById.get(String(docId).toLowerCase());
                const displayName = doc ? getDocumentDisplayName(doc) : null;
                const showTypeChip = doc?.documentTypeName && doc.documentTypeName !== displayName;

                return (
                  <Stack key={docId} direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1.25 }}>
                    <DescriptionIcon fontSize="small" color="action" sx={{ mt: 0.35 }} />
                    <Box sx={{ minWidth: 0 }}>
                      {docsLoading && !doc ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={14} />
                          <Typography variant="body2" color="text.secondary">Loading…</Typography>
                        </Stack>
                      ) : (
                        <>
                          <Typography variant="body2" fontWeight={600}>{displayName || 'Document'}</Typography>
                          {doc && (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                              {showTypeChip && (
                                <Chip label={doc.documentTypeName} size="small" variant="outlined" color="secondary" />
                              )}
                              {doc.category && (
                                <Chip label={doc.category} size="small" variant="outlined" color="info" />
                              )}
                            </Stack>
                          )}
                          {!doc && !docsLoading && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              Not found in this loan's document list — ID may be stale or from another source.
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block', mt: 0.5 }}>
                            {docId}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Stack>
                );
              })}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ConditionsTab;
